import { useQuery } from '@tanstack/react-query';
import { type ComponentProps, useCallback, useEffect, useState } from 'react';
import { usePreviousDistinct } from 'react-use';

import useAppDispatch from '~hooks/useAppDispatch';
import useAssets from '~hooks/useAssets';
import useDelayFn from '~hooks/useDelayFn';
import { queries } from '~modules/media-library/controller/api/Queries';
import { type AssetId } from '~modules/media-library/model/Asset';
import { type CropDownloadDto, type CropFormatDto } from '~modules/media-library/model/CropFormat';
import { CropModal } from '~modules/media-library/view/CropModal/CropModal';
import { StatusActions } from '~store/actions';
import { makeProgramaticallyLinkClick } from '~utils/link';
import { hasOwnProperty } from '~utils/object';

type CropModalControllerProps = Resolve<
  {
    assetId: AssetId;
  } & Pick<ComponentProps<typeof CropModal>, 'onClose'>
>;

export function CropModalController({ assetId, onClose }: CropModalControllerProps) {
  const dispatch = useAppDispatch();
  const { getAssetById, setAsset } = useAssets();
  const [isOpen, setIsOpen] = useState(false);
  const delayedOnClose = useDelayFn(onClose, 500);
  const [workCropFormat, setWorkCropFormat] = useState<CropFormatDto>(null);
  const [selectedCropFormat, setSelectedCropFormat] = useState(0);
  const [cropDownloadRequest, setCropDownloadRequest] = useState<CropDownloadDto>();

  // Derived values
  const isProcessing = Boolean(cropDownloadRequest?.assetId);

  // get asset from store
  const asset = getAssetById(assetId);
  const prevAsset = usePreviousDistinct(asset, (_, next) => !Boolean(next));

  // load details and update asset in asset store
  const { data: assetData } = useQuery({
    ...queries.mediaLibrary.details(assetId),
    enabled: Boolean(assetId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (assetData === undefined) return;
    setAsset(assetData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetData]);

  // load crop formats for assets
  const { data: cropFormatList } = useQuery({
    ...queries.mediaLibrary.cropFormats(assetId),
    enabled: Boolean(assetId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (cropFormatList === undefined) return;
    setWorkCropFormat(cropFormatList[0]);
  }, [cropFormatList]);

  // load cropped
  const { data: croppedAsset } = useQuery({
    ...queries.mediaLibrary.croppedAsset(cropDownloadRequest),
    enabled: isProcessing,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (croppedAsset === undefined) return;
    if (!hasOwnProperty(croppedAsset, 'crop')) {
      // cropping failed
      setCropDownloadRequest(undefined);
      dispatch(StatusActions.setErrorNotification('Image cropping failed'));
      return;
    }
    makeProgramaticallyLinkClick({
      href: croppedAsset['crop'].downloadUrl,
      onClick() {
        setCropDownloadRequest(undefined);
      },
    });
  }, [croppedAsset, dispatch]);

  // open details modal when a asset is set
  useEffect(() => {
    if (Boolean(asset)) {
      setSelectedCropFormat(0);
      setCropDownloadRequest(undefined);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [asset]);

  // Handler: close dialog, but delay the closing to have smoother transitions
  const handleOnClose = useCallback(() => {
    setIsOpen(false);
    delayedOnClose();
  }, [delayedOnClose]);

  const handleCustomWidthChanged = useCallback(
    (width: number) => {
      // Check if the input is empty or not a number
      if (isNaN(width) || width === undefined) {
        width = null;
      }
      setSelectedCropFormat(0);
      setWorkCropFormat({ ...workCropFormat, width: width });
    },
    [workCropFormat],
  );

  const handleCustomHeightChanged = useCallback(
    (height: number) => {
      // Check if the input is empty or not a number
      if (isNaN(height) || height === undefined) {
        height = null;
      }

      setSelectedCropFormat(0);
      setWorkCropFormat({ ...workCropFormat, height: height });
    },
    [workCropFormat],
  );

  const handleOnSelectCropFormat = useCallback(
    (format: number) => {
      setSelectedCropFormat(format);
      setWorkCropFormat(cropFormatList[format]);
    },
    [cropFormatList],
  );

  // Handler: download request callback
  const handleDownloadRequest = useCallback(async (options: CropDownloadDto) => {
    setCropDownloadRequest(options);
  }, []);

  return (
    <CropModal
      isOpen={isOpen}
      asset={asset || prevAsset}
      onClose={handleOnClose}
      cropFormats={cropFormatList}
      cropFormat={workCropFormat}
      selectedCropFormat={selectedCropFormat}
      onSelectCropFormat={handleOnSelectCropFormat}
      onCustomWidthChanged={handleCustomWidthChanged}
      onCustomHeightChanged={handleCustomHeightChanged}
      onDownload={handleDownloadRequest}
      isProcessing={isProcessing}
    />
  );
}
